/*
 * Birthday-campaign party cap — responsive "end of line 1" placement.
 *
 * The cap must rest above the LAST character of the FIRST rendered line of a product
 * title, at every viewport width and font size, without joining the text flow or causing
 * layout shift. No CSS can anchor to the end of a WRAPPED line's text: an inline-block box
 * grows to the widest line (a corner-anchored cap floats into empty space), and an inline
 * anchor lands on the last line, not the first. So we measure the first line's client rect
 * with a Range and position the (absolutely-positioned, out-of-flow) cap there, re-measuring
 * whenever the title reflows.
 *
 * Contract (owned by snippets/birthday-cap.liquid, mode 'line1'):
 *   - each cap is `img.birthday-cap[data-cap-line1]` and is the LAST child of its title;
 *   - the title element is position:relative (the cap's containing block);
 *   - the cap starts visibility:hidden and is revealed once positioned, so a no-JS visitor
 *     sees nothing rather than a mispositioned cap (the cap is purely decorative).
 * Authoring knobs read off the cap: data-cap-rot (deg), data-cap-nudgex / -nudgey (px).
 */
(function () {
  var SELECTOR = 'img.birthday-cap[data-cap-line1]';
  var observedTitles = 'WeakSet' in window ? new WeakSet() : null;
  var resizeObserver = null;
  var raf = 0;

  function firstLineRect(title, cap) {
    var range = document.createRange();
    range.selectNodeContents(title);
    // Exclude the cap itself so the measured rects are text-only.
    try {
      range.setEndBefore(cap);
    } catch (e) {
      return null;
    }
    var rects = range.getClientRects();
    return rects.length ? rects[0] : null;
  }

  function place(cap) {
    var title = cap.parentElement;
    if (!title) return;
    var line1 = firstLineRect(title, cap);
    if (!line1 || line1.width === 0) {
      cap.style.visibility = 'hidden';
      return;
    }
    var host = title.getBoundingClientRect();
    var rot = parseFloat(cap.getAttribute('data-cap-rot')) || 0;
    var nx = parseFloat(cap.getAttribute('data-cap-nudgex')) || 0;
    var ny = parseFloat(cap.getAttribute('data-cap-nudgey')) || 0;
    var seat = parseFloat(cap.getAttribute('data-cap-seat'));
    if (isNaN(seat)) seat = 0.35;
    // left/top place the cap's own origin at the end of line 1 (titles carry no border,
    // so the border-box origin from getBoundingClientRect equals the padding edge that
    // absolute offsets resolve against). `seat` (a fraction of the measured line height)
    // drops the anchor DOWN into the line so the cap rests just above the glyphs instead
    // of floating a full leading-gap above them — proportional, so it looks the same on a
    // small card title and a large PDP title without per-surface pixels.
    cap.style.left = line1.right - host.left + 'px';
    cap.style.top = line1.top - host.top + seat * line1.height + 'px';
    // -50% X centres the cap on the final glyph's end; -100% Y lifts the cap onto the
    // (seated) anchor so it sits just above the text. Nudges + rotation are fine knobs.
    cap.style.transform =
      'translate(calc(-50% + ' + nx + 'px), calc(-100% + ' + ny + 'px)) rotate(' + rot + 'deg)';
    cap.style.transformOrigin = 'center bottom';
    cap.style.visibility = 'visible';
  }

  function placeAll() {
    var caps = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < caps.length; i++) place(caps[i]);
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(function () {
      raf = 0;
      placeAll();
    });
  }

  // A title reflows (viewport/tablet/mWeb width change, webfont swap, 1<->2<->3 line wrap)
  // by changing its own box size, so a per-title ResizeObserver is the single reliable
  // "line 1 may have moved" trigger. The cap is out-of-flow, so revealing it never feeds
  // back into the observed title's size.
  function observe(cap) {
    var title = cap.parentElement;
    if (!title || !resizeObserver || !observedTitles || observedTitles.has(title)) return;
    observedTitles.add(title);
    resizeObserver.observe(title);
  }

  function observeAll() {
    if (!resizeObserver) return;
    var caps = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < caps.length; i++) observe(caps[i]);
  }

  function init() {
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(schedule);
      observeAll();
    }
    placeAll();

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

    // Collection pages lazy-load extra product groups over AJAX; pick up caps added later.
    if (window.MutationObserver) {
      new MutationObserver(function (mutations) {
        var sawNodes = false;
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            sawNodes = true;
            break;
          }
        }
        if (sawNodes) {
          observeAll();
          schedule();
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
